import Link from 'next/link';
import { toRelativePath } from '@/lib/seo/breadcrumbs';
import type { BreadcrumbItem } from '@/lib/seo/schemas';

export type BreadcrumbVariant = 'default' | 'light' | 'accent';

const VARIANT_STYLES: Record<
  BreadcrumbVariant,
  { container: string; link: string; current: string; separator: string }
> = {
  default: {
    container: 'bg-white border border-gray-200 shadow-[0_4px_12px_rgba(0,0,0,0.05)] px-4 py-1.5 rounded-full text-gray-900',
    link: 'text-gray-600 hover:text-[#54169C] transition-colors duration-200',
    current: 'text-[#54169C] font-semibold',
    separator: 'text-gray-400',
  },
  light: {
    container: 'bg-white border border-gray-200 shadow-[0_4px_12px_rgba(0,0,0,0.05)] px-4 py-1.5 rounded-full text-gray-900',
    link: 'text-gray-600 hover:text-black transition-colors duration-200',
    current: 'text-black font-semibold',
    separator: 'text-gray-400',
  },
  accent: {
    container: 'bg-white border border-gray-200 shadow-[0_4px_12px_rgba(0,0,0,0.05)] px-4 py-1.5 rounded-full text-gray-900',
    link: 'text-gray-600 hover:text-[#F26522] transition-colors duration-200',
    current: 'text-[#F26522] font-semibold',
    separator: 'text-gray-400',
  },
};

interface BreadcrumbsProps {
  items: BreadcrumbItem[];
  variant?: BreadcrumbVariant;
  className?: string;
}

export function Breadcrumbs({
  items,
  variant = 'default',
  className = '',
}: BreadcrumbsProps) {
  if (items.length <= 1) return null;

  const styles = VARIANT_STYLES[variant];

  return (
    <nav aria-label="Breadcrumb" className={`inline-block ${className}`}>
      <ol
        className={`flex flex-wrap items-center gap-x-2 gap-y-1 text-xs sm:text-sm font-medium tracking-wide ${styles.container}`}
      >
        {items.map((item, index) => {
          const isLast = index === items.length - 1;

          return (
            <li key={item.url} className="flex items-center gap-2">
              {index > 0 && (
                <span
                  aria-hidden="true"
                  className={`${styles.separator} flex items-center justify-center`}
                >
                  <svg
                    className="w-3 h-3 stroke-current shrink-0"
                    fill="none"
                    strokeWidth="2.5"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </span>
              )}
              {isLast ? (
                <span className={styles.current} aria-current="page">
                  {item.name}
                </span>
              ) : (
                <Link
                  href={toRelativePath(item.url)}
                  className={`transition-colors ${styles.link}`}
                >
                  {item.name}
                </Link>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

